/**
 * Global PWA install handler.
 *
 * Chrome on Android fires `beforeinstallprompt` exactly once per page session.
 * We must capture it at app startup (outside any React lifecycle), keep the
 * single event reference, and let any UI component trigger the native install
 * dialog by calling `triggerInstall()`. After the user picks Install/Cancel,
 * the event is consumed and the saved reference is cleared.
 */

let deferredPrompt = null;
const listeners = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((cb) => {
      try { cb(true); } catch { /* ignore listener errors */ }
    });
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    listeners.forEach((cb) => {
      try { cb(false); } catch { /* ignore listener errors */ }
    });
  });
}

export const getInstallPrompt = () => deferredPrompt;
export const hasInstallPrompt = () => deferredPrompt !== null;

export const subscribeInstallPrompt = (callback) => {
  listeners.add(callback);
  // Fire immediately with current state so subscribers don't miss
  // an event that already arrived before they mounted.
  try { callback(deferredPrompt !== null); } catch { /* ignore */ }
  return () => listeners.delete(callback);
};

export const triggerInstall = async () => {
  if (!deferredPrompt) return { available: false, outcome: null };
  try {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    listeners.forEach((cb) => { try { cb(false); } catch { /* ignore */ } });
    return { available: true, outcome };
  } catch {
    return { available: false, outcome: null };
  }
};

export const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);
